import { CssBaseline, LinkProps, ThemeProvider, createTheme } from '@mui/material'
import { ErrorFallback } from '@src/shared/components/ErrorFallback'
import { interceptError } from '@src/shared/services/http-common'
import { AxiosError } from 'axios'
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react'
import { BrowserRouter as Router, Link as RouterLink, LinkProps as RouterLinkProps } from 'react-router-dom'
import {
  CACHED_THEME_MODE,
  ColorModeContext,
  PREFERS_MODE_SETTING,
  useCustomTheme,
  type ThemeMode,
} from '@src/contexts/ThemeContext'
import { AppContext } from './contexts/appContext '
import { useUser } from '@src/shared/contexts/UserContext'
import LoginPage from './Authentication/LoginPage'
import AppHeader from './components/AppHeader'
import { SplashScreen, SPLASH_SEEN_KEY } from './components/SplashScreen'
import TopLevelRoutes from './Routes'

const LinkBehavior = forwardRef<HTMLAnchorElement, Omit<RouterLinkProps, 'to'> & { href: RouterLinkProps['to'] }>(
  (props, ref) => {
    const { href, ...other } = props
    // Map href (MUI) -> to (react-router)
    return <RouterLink ref={ref} to={href} {...other} />
  },
)

LinkBehavior.displayName = 'LinkBehavior'

const App = () => {
  const [error, setError] = useState<AxiosError | Error | null>(null)
  const [mode, setMode] = useState<ThemeMode>(CACHED_THEME_MODE)
  const baseTheme = useCustomTheme(mode)

  const isAuthRedirect = window.location.pathname === '/auth-page'
  const { needAuth } = useUser()
  const [showSplash, setShowSplash] = useState(
    () => !needAuth && !sessionStorage.getItem(SPLASH_SEEN_KEY),
  )

  const handleSplashComplete = useCallback(() => {
    sessionStorage.setItem(SPLASH_SEEN_KEY, '1')
    setShowSplash(false)
  }, [])

  // Merge LinkBehavior overrides into the themed base
  const theme = useMemo(
    () =>
      createTheme(baseTheme, {
        components: {
          MuiLink: {
            defaultProps: {
              component: LinkBehavior,
            } as LinkProps,
          },
          MuiButtonBase: {
            defaultProps: {
              LinkComponent: LinkBehavior,
            },
          },
        },
      }),
    [baseTheme],
  )

  const colorMode = useMemo(
    () => ({
      mode,
      toggleColorMode: () => {
        setMode((prev) => {
          const next: ThemeMode = prev === 'light' ? 'dark' : 'light'
          localStorage.setItem(PREFERS_MODE_SETTING, next)
          return next
        })
      },
    }),
    [mode],
  )

  const appContext = useMemo(() => ({ toggleColorMode: colorMode.toggleColorMode, error }), [colorMode, error])

  // Expose the current mode on <body data-theme-mode="..."> for CSS-based dark mode styles
  useEffect(() => {
    document.body.setAttribute('data-theme-mode', mode)
  }, [mode])

  interceptError(setError)

  return (
    <>
      <ColorModeContext.Provider value={colorMode}>
        <ThemeProvider theme={theme}>
          <AppContext.Provider value={appContext}>
            {error && <ErrorFallback error={error} resetError={setError} />}
            <CssBaseline />
            <Router>
              {needAuth && !isAuthRedirect && <LoginPage />}
              {!needAuth && (
                <>
                  {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
                  <AppHeader hasAlertBanner={false} />
                  <TopLevelRoutes />
                </>
              )}
            </Router>
          </AppContext.Provider>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </>
  )
}

export default App
